/* eslint-disable max-lines */
import React, {
    createContext, useState, useEffect, useContext
} from 'react';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    modelService
} from 'shared/api/services/BEX/models.services';
import {
    GetLLMModelsRequest,
    InstructionEntity,
    LLMModelUnion,
} from 'shared/api/services/bexService.types';
import {
    Opcode,
    ContextVar
} from 'builders/builderOpcode/types/newOpcodeTypes.types';

// add types for the context
export const OpCodeBuilderContext = createContext<{
    opCodes: Opcode[];
    isOpcodeLoading: boolean;
    selectedOpCode: string;
    constantStepTypes: {
        id: number;
        name: string;
    }[];
    orgId: string | null;
    subOrgId: string | null;
    instructions: InstructionEntity[];
    modelProviders: LLMModelUnion[];
    handleReloadModels: (hardRefresh?: boolean) => Promise<LLMModelUnion[]>;
    IsInstructionLoading: boolean;
    removedContext: string;
    indicators: {
        step_id: string;
        step_type: string;
        step_number: string;
    }[];
    contextVars: ContextVar[];
    actions: (action: { type: string; payload: any }) => void;
    fetchInstructions: () => Promise<void>;
}>({} as any);

export const DEFAULT_LOOP_CONTEXTS: string[] = [
    'range_start',
    'range_end',
    'range_step',
];

export const STEP_TYPES: {
    id: number;
    name: string;
}[] = [
    {
        id: 0,
        name: 'Identification'
    },
    {
        id: 1,
        name: 'LLM'
    },
    {
        id: 2,
        name: 'Non-LLM'
    },
    {
        id: 3,
        name: 'Condition'
    },
    {
        id: 4,
        name: 'Loop'
    },
    {
        id: 5,
        name: 'Memory'
    },
    {
        id: 6,
        name: 'API'
    },
];

export const initialOpCodeSteps = [{
    step_id: window?.crypto?.randomUUID(),
    step_type: 'Identification',
    input_params: {
        unique_name: '',
        model_type: '',
        query: '',
    },
    output_params: {
        output_name: ''
    },
}];

export const reservedKeys: string[] = [
    'real_time_data',
    'collection_id',
    'org_id',
    'sub_org_id',
    ...DEFAULT_LOOP_CONTEXTS
];

export const operatorOptions: {
    value: string;
    label: string;
}[] = [
    {
        value: '>',
        label: 'Greater than'
    },
    {
        value: '<',
        label: 'Less than'
    },
    {
        value: '==',
        label: 'Equal to'
    },
    {
        value: '>=',
        label: 'Greater than or equal to'
    },
    {
        value: '<=',
        label: 'Less than or equal to'
    },
    {
        value: '!=',
        label: 'Not equal to'
    },
    {
        value: 'in',
        label: 'In'
    },
    {
        value: 'not in',
        label: 'Not in'
    },
];

export const OpCodeBuilderProvider = ({
    organizationId = null, subOrganizationId = null, children
}: {
    organizationId?: string | null,
    subOrganizationId?: string | null,
    children: React.ReactNode
}): React.ReactElement => {
    const [opCodes, setOpCodes] = useState<Opcode[]>([]);
    const [isOpcodeLoading, setIsOpcodeLoading] = useState<boolean>(false);
    const [selectedOpCode, setSelectedOpCode] = useState<string>('');
    const [constantStepTypes, setConstantStepTypes] = useState<{
        id: number;
        name: string;
    }[]>(STEP_TYPES);
    const {
        selectedOrganization
    } = useContext(OrganizationContext) as any;
    const {
        selectedSubOrganization
    } = useContext(SubOrgContext) as any;
    const [removedContext, setRemovedContext] = useState<string>(null);
    const [instructions, setInstructions] = useState<InstructionEntity[]>([]);
    const [modelProviders, setModelProviders] = useState<LLMModelUnion[]>([]);
    const [IsInstructionLoading, setIsInstructionLoading] = useState<boolean>(true);
    const [indicators, setIndicators] = useState<{
        step_id: string;
        step_type: string;
        step_number: string;
    }[]>([]);
    const [contextVars, setContextVars] = useState<ContextVar[]>([
        {
            name: 'real_time_data',
            type: 'any',
            preFilled: true
        }
    ]);

    const orgId = organizationId ? organizationId : selectedOrganization?._id;
    const subOrgId = subOrganizationId ? subOrganizationId : selectedSubOrganization?._id;

    const actions = (action: {
        type: string;
        payload: any;
    }) => {
        const {
            type, payload
        } = action;
        switch (type) {
            case 'SET_OP_CODES':
                return setOpCodes(payload);
            case 'SET_SELECTED_OPCODE':
                return setSelectedOpCode(payload);
            case 'SET_CONSTANT_TYPES':
                return setConstantStepTypes(payload);
            case 'SET_REMOVED_CONTEXT':
                return setRemovedContext(payload);
            case 'SET_INSTRUCTIONS':
                return setInstructions(payload);
            case 'SET_INDICATORS':
                return setIndicators(payload);
            case 'SET_IS_OPCODE_LOADING':
                return setIsOpcodeLoading(payload);
            case 'SET_CONTEXT_VARS':
                return setContextVars(payload);
            default:
                console.error(`Unhandled action type: ${type}`);
                return null;
        }
    };

    const handleReloadModels = async (hardRefresh = false) => {
        const body: GetLLMModelsRequest = {
            provider: selectedOrganization.providers || [],
            filter: {
                Watson: {
                    region: 'All',
                    showInactive: false
                },
                Groq: {
                    showInactive: false
                }
            }
        };
        const res = await modelService.getModels(body, hardRefresh, orgId);
        const models = res.data || [];
        models.sort((a, b) => {
            let providerComparison = a.provider_type.localeCompare(b.provider_type);
            return providerComparison !== 0 ? providerComparison : a.model_id.localeCompare(b.model_id);
        });
        setModelProviders(models);

        return models;
    };

    const fetchInstructions = async () => {
        try {
            setInstructions([]); // Reset instructions
            setIsInstructionLoading(true);

            const models = await handleReloadModels();
            const modelIdSet = new Set(models.map(model => model.model_id));

            const res = await instructionService.listModels(
                orgId,
                subOrgId,
            );

            const instructios = res.data || [];
            const fetchedInstructions = instructios.map((instruction) => {
                const modifiedByMember = selectedOrganization.members.find(member => member.id._id === instruction.modified_by);
                const userIdMember = selectedOrganization.members.find(member => member.id._id === instruction.createdBy);

                const parameters = instruction.parameters || {};
                // update the parameters for IBM model from sampling to sample
                if (
                    instruction.model_type === 'IBM' &&
                    parameters &&
                    'decoding_method' in parameters &&
                    (parameters as { decoding_method?: string }).decoding_method === 'sampling'
                ) {
                    (parameters as { decoding_method?: string }).decoding_method = 'sample';
                }

                return {
                    ...instruction,
                    parameters,
                    modified_by: modifiedByMember ? modifiedByMember.id.name : instruction.modified_by,
                    user_id: userIdMember ? userIdMember.id.name : instruction.createdBy,
                    isModelProviderExist: modelIdSet.has(instruction.modelId)
                };
            });

            setInstructions(fetchedInstructions);
        } catch (error) {
            console.error('Error fetching instructions:', error);
        } finally {
            setIsInstructionLoading(false);
        }
    };

    useEffect(() => {
        fetchInstructions();
    }, []);

    const value = {
        opCodes,
        isOpcodeLoading,
        selectedOpCode,
        constantStepTypes,
        orgId,
        subOrgId,
        instructions,
        modelProviders,
        handleReloadModels,
        IsInstructionLoading,
        removedContext,
        indicators,
        contextVars,
        actions,
        fetchInstructions
    };

    return (
        <OpCodeBuilderContext.Provider value={value}>
            {children}
        </OpCodeBuilderContext.Provider>
    );
};